import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Statistic, Descriptions, Progress, Button, Select, InputNumber, Table, Tag, Popconfirm, message, Typography, Space } from 'antd';
import { ReloadOutlined, DownloadOutlined, CloudUploadOutlined } from '@ant-design/icons';
import { useFetchWrapper } from '_helpers';

export { MaintenanceTab };

const { Text, Title } = Typography;
const { Option } = Select;

function fmtUptime(sec) {
    if (!sec && sec !== 0) return '—';
    const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600), m = Math.floor((sec % 3600) / 60);
    return `${d ? d + 'n ' : ''}${h}g ${m}p`;
}

function MaintenanceTab() {
    const fetchWrapper = useFetchWrapper();
    const [health, setHealth] = useState(null);
    const [logName, setLogName] = useState('pm2');
    const [logLines, setLogLines] = useState(200);
    const [log, setLog] = useState(null);
    const [logLoading, setLogLoading] = useState(false);
    const [backups, setBackups] = useState(null);
    const [busy, setBusy] = useState(false);

    const loadHealth = useCallback(async () => {
        try {
            const res = await fetchWrapper.get('/api/admin/health', null, null);
            const json = await res.json();
            if (json.status === 'Success') setHealth(json.message);
        } catch (_) { /* transient */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadBackups = useCallback(async () => {
        try {
            const res = await fetchWrapper.get('/api/admin/backups', null, null);
            const json = await res.json();
            if (json.status === 'Success') setBackups(json.message);
        } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadHealth(); loadBackups();
        const t = setInterval(loadHealth, 15000);   // live-ish health without hammering
        return () => clearInterval(t);
    }, [loadHealth, loadBackups]);

    async function loadLog() {
        setLogLoading(true);
        try {
            const res = await fetchWrapper.get(`/api/admin/logs?name=${logName}&lines=${logLines}`, null, null);
            const json = await res.json();
            if (json.status === 'Success') setLog(json.message);
            else message.error(json.message || 'Không tải được log');
        } catch (_) { message.error('Không tải được log'); }
        setLogLoading(false);
    }

    async function runBackup() {
        setBusy(true);
        try {
            const res = await fetchWrapper.post('/api/admin/backup', null, null);
            const json = await res.json();
            message[json.status === 'Success' ? 'success' : 'error'](json.message || 'Xong');
            setTimeout(loadBackups, 8000);   // give the dump a head start, then refresh the list
        } catch (_) { message.error('Không chạy được backup'); }
        setBusy(false);
    }

    const m = health?.memory;
    const disk = health?.disk;

    return (
        <div>
            {/* ── Health ─────────────────────────────────────────────── */}
            <Card
                title="Tình trạng máy chủ"
                extra={<Button size="small" icon={<ReloadOutlined />} onClick={loadHealth}>Làm mới</Button>}
                style={{ marginBottom: 16 }}
            >
                {!health ? <Text type="secondary">Đang tải…</Text> : (
                    <Row gutter={[24, 16]}>
                        <Col xs={24} sm={12} md={8}>
                            <Text strong>RAM</Text>
                            <Progress percent={m?.usedPct} size="small"
                                status={m?.usedPct > 90 ? 'exception' : 'normal'} />
                            <Text type="secondary">{m?.freeMB} MB trống / {m?.totalMB} MB · app {m?.processRssMB} MB</Text>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Text strong>Ổ đĩa</Text>
                            {disk ? <>
                                <Progress percent={disk.usedPct} size="small"
                                    status={disk.usedPct > 90 ? 'exception' : 'normal'} />
                                <Text type="secondary">{disk.freeMB} MB trống / {disk.totalMB} MB</Text>
                            </> : <div><Text type="secondary">Không có dữ liệu</Text></div>}
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Statistic title="Uptime tiến trình" value={fmtUptime(health.processUptimeSec)} />
                        </Col>
                        <Col span={24}>
                            <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3 }}>
                                <Descriptions.Item label="Node">{health.node}</Descriptions.Item>
                                <Descriptions.Item label="MongoDB">
                                    {health.mongo?.connected
                                        ? <Tag color="green">kết nối · v{health.mongo.version || '?'}</Tag>
                                        : <Tag color="red">mất kết nối</Tag>}
                                </Descriptions.Item>
                                <Descriptions.Item label="Load (1/5/15p)">{health.loadAvg?.join(' / ')}</Descriptions.Item>
                                <Descriptions.Item label="Git commit">{health.git?.commit || '—'}</Descriptions.Item>
                                <Descriptions.Item label="Nhánh">{health.git?.branch || '—'}</Descriptions.Item>
                            </Descriptions>
                        </Col>
                    </Row>
                )}
            </Card>

            {/* ── Logs ───────────────────────────────────────────────── */}
            <Card title="Xem log" style={{ marginBottom: 16 }}>
                <Space wrap style={{ marginBottom: 12 }}>
                    <Select value={logName} onChange={setLogName} style={{ width: 160 }}>
                        <Option value="pm2">App (pm2)</Option>
                        <Option value="backup">Backup</Option>
                        <Option value="error">Lỗi (error)</Option>
                    </Select>
                    <InputNumber min={20} max={500} value={logLines} onChange={setLogLines} addonAfter="dòng" />
                    <Button type="primary" loading={logLoading} onClick={loadLog}>Tải log</Button>
                </Space>
                {log && (
                    <pre style={{ background: 'var(--bg-subtle)', color: 'var(--ink)', border: '1px solid var(--line)', padding: 12, borderRadius: 6, maxHeight: 380, overflow: 'auto', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                        {log.lines || '(trống)'}
                    </pre>
                )}
            </Card>

            {/* ── Backups ────────────────────────────────────────────── */}
            <Card
                title="Sao lưu"
                extra={
                    <Popconfirm title="Chạy backup ngay bây giờ?" onConfirm={runBackup}>
                        <Button size="small" type="primary" icon={<CloudUploadOutlined />} loading={busy}>Chạy backup</Button>
                    </Popconfirm>
                }
            >
                {backups && !backups.configured
                    ? <Text type="secondary">Google Drive backup chưa được cấu hình (GOOGLE_BACKUP_FOLDER_ID).</Text>
                    : (
                        <Table
                            rowKey="id"
                            size="small"
                            dataSource={backups?.files || []}
                            pagination={{ pageSize: 10 }}
                            locale={{ emptyText: 'Chưa có bản sao lưu' }}
                            columns={[
                                { title: 'Tên tệp', dataIndex: 'name' },
                                { title: 'Kích thước', dataIndex: 'sizeMB', render: v => v != null ? `${v} MB` : '—', width: 120 },
                                { title: 'Ngày tạo', dataIndex: 'createdTime', render: v => v ? new Date(v).toLocaleString('vi-VN') : '—', width: 200 },
                                {
                                    title: '', width: 120, render: (_, r) => (
                                        <a href={`/api/admin/backups/${r.id}/download?name=${encodeURIComponent(r.name)}`}>
                                            <Button size="small" icon={<DownloadOutlined />}>Tải</Button>
                                        </a>
                                    ),
                                },
                            ]}
                        />
                    )}
            </Card>
        </div>
    );
}
